/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
 * distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
 * prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this
 * content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import { useTranslation } from 'react-i18next'
import { TrashIcon } from '@heroicons/react/24/solid'
import UiInput from 'components/Ui/UiInput'
import { ReservationPlan } from 'types/entities/ReservationPlan'

interface ReservationPlannerModalItemProps {
    index: number
    plan: Partial<ReservationPlan>
    formatTimeToHHMM: (minutes: number) => string
    parseHHMMToMinutes: (hhmm: string) => number
    handleChange: <K extends keyof ReservationPlan>(
        index: number,
        field: K,
        value: ReservationPlan[K]
    ) => void
    handleRemoveRow: (index: number) => void
}

const ReservationPlannerModalItem: React.FC<
    ReservationPlannerModalItemProps
> = ({
    index,
    plan,
    formatTimeToHHMM,
    parseHHMMToMinutes,
    handleChange,
    handleRemoveRow,
}) => {
    const { t } = useTranslation()

    return (
        <div className="ReservationPlannerModalItem grid grid-cols-12 gap-2 items-center">
            <div className="col-span-8">
                <UiInput
                    className="h-8! text-xs"
                    value={plan.name || ''}
                    onChange={(e) =>
                        handleChange(index, 'name', e.target.value)
                    }
                    placeholder={t('Activity...')}
                />
            </div>
            <div className="col-span-3">
                <UiInput
                    className="h-8! text-xs pr-8!"
                    type="time"
                    value={formatTimeToHHMM(plan.time || 0)}
                    onChange={(e) =>
                        handleChange(
                            index,
                            'time',
                            parseHHMMToMinutes(e.target.value)
                        )
                    }
                />
            </div>
            <div className="col-span-1 text-center">
                <button
                    onClick={() => handleRemoveRow(index)}
                    className="text-red-500 hover:text-red-700 transition-colors p-1"
                >
                    <TrashIcon className="w-4 h-4" />
                </button>
            </div>
        </div>
    )
}

export default ReservationPlannerModalItem
